import React, { useEffect, useRef, useState } from "react";
import { Button, NoticeBox } from "../../ui";
import ApplicationCard from "./ApplicationCard";
import AdminQueueModal from "./AdminQueueModal";
import useApplicationFlows from "./useApplicationFlows";
import "./Applications.css";

/*
  Applications of the selected store, one compact card per supported delivery
  app (the list comes from the server, which takes it from the canonical
  integrations vocabulary; future platforms never appear).

  The card shows the real state, last month's revenue only when report data
  exists, and active / ALL store branches. One button opens the flow that fits
  the state. The flows themselves (modals, requests, review) live in
  useApplicationFlows and are shared with the Branches page. Every action is
  authorized again by the backend.
*/
export default function Applications({ lang, storeId, role, onChanged, onGoToCompletion, onSelectStore, onHiddenChange }) {
  const flows = useApplicationFlows({ lang, storeId, role, onChanged, onGoToCompletion });
  const { t, ar, token, canManage, canReview, lock, overview, load, open } = flows;
  const [queueOpen, setQueueOpen] = useState(false);
  const pendingOpen = useRef(null);

  // "Review" in the administrator queue may point at another store: switch to it,
  // then open the application once that store's cards are loaded.
  const reviewFromQueue = (reviewStoreId, appKey, requestId = null) => {
    setQueueOpen(false);
    if (reviewStoreId !== storeId) {
      pendingOpen.current = { appKey, requestId };
      onSelectStore?.(reviewStoreId);
      return;
    }
    const app = overview.data?.applications.find((item) => item.key === appKey);
    if (app) open("manage", app, { reviewId: requestId });
  };

  useEffect(() => {
    if (!pendingOpen.current || overview.status !== "ready") return;
    const target = pendingOpen.current;
    const app = overview.data.applications.find((item) => item.key === target.appKey);
    pendingOpen.current = null;
    if (app) open("manage", app, { reviewId: target.requestId || null });
  }, [overview, open]);

  const apps = overview.data?.applications || [];
  // Without an active subscription (`lock`) the section is useful only when it has history to show: an application that was ever
  // requested, activated, rejected or closed, or a pending request. Loading and errors are never hidden.
  const hasHistory = apps.some((app) => app.state !== "notActivated" || app.pending?.count > 0 || app.activeBranches > 0);
  const hidden = Boolean(lock) && overview.status === "ready" && !hasHistory;
  useEffect(() => {
    onHiddenChange?.(hidden);
  }, [hidden, onHiddenChange]);
  if (hidden) return null;

  return (
    <section className="lud-dashboard-section lud-applications" dir={ar ? "rtl" : "ltr"}>
      <div className="lud-dashboard-section-title lud-applications-title">
        <h2>{t.title}</h2>
        {canReview && (
          <Button size="sm" variant="neutral" appearance="soft" onClick={() => setQueueOpen(true)}>
            {t.queue}
          </Button>
        )}
      </div>

      {overview.status === "loading" && (
        <p className="lud-applications-state" role="status">{t.loading}</p>
      )}
      {overview.status === "error" && (
        <NoticeBox variant="danger" size="sm">
          {overview.error}{" "}
          <Button size="sm" variant="neutral" appearance="soft" onClick={() => load()}>{t.retry}</Button>
        </NoticeBox>
      )}
      {overview.status === "ready" && apps.length === 0 && <p className="lud-applications-state">{t.empty}</p>}

      {overview.status === "ready" && apps.length > 0 && (
        <div className="lud-applications-grid">
          {apps.map((app) => (
            <ApplicationCard key={app.key} app={app} lang={lang} t={t} canManage={canManage} canReview={canReview} onOpen={open} />
          ))}
        </div>
      )}

      {flows.element}
      {canReview && <AdminQueueModal open={queueOpen} lang={lang} t={t} token={token} onClose={() => setQueueOpen(false)} onReview={reviewFromQueue} />}
    </section>
  );
}
